import { Button } from '@edsn/pie-components';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { z } from 'zod';
import { CancelCreateTicketDialog } from '#pie/components/cancel-create-ticket-dialog/CancelCreateTicketDialog';
import { Card } from '#pie/components/card/Card';
import { Divider } from '#pie/components/divider/Divider';
import { Message } from '#pie/components/message/Message';
import { Page, PageHeader } from '#pie/components/page/Page';
import { PageHead } from '#pie/components/page-head/PageHead';
import { RelatedKnowledgeArticlesCard } from '#pie/components/related-knowledge-articles-card/RelatedKnowledgeArticlesCard';
import { Stack } from '#pie/components/stack/Stack';
import { Text } from '#pie/components/text/Text';
import { ToastButton } from '#pie/components/toast/Toast';
import { useToast } from '#pie/components/toast/ToastContext';
import { useRelatedKnowledgeArticles } from '#pie/hooks/useRelatedKnowlegdeArticles';
import { useGetMainCategory, usePostTicket } from '#pie/internal/api/pie-bff';
import { useStoredParams } from '#pie/stores/searchParams';
import { isValidXmlCharacters } from '#pie/utils/isValidXmlCharacters';
import { typedFormFields } from '#pie/utils/typedFormFields';
import {
  assigneeValidationSchema,
  TicketAssigneeCard,
} from '../TicketAssigneeCard';
import {
  attachmentValidationSchema,
  TicketAttachmentFields,
} from '../TicketAttachmentFields';
import {
  categoriesValidationSchema,
  TicketCategoriesFields,
} from '../TicketCategoriesFields';
import { TicketDescription } from '../TicketDescription';
import {
  gridOperatorValidationSchema,
  TicketGridOperatorsFields,
} from '../TicketGridOperatorsFields';
import { TicketTemplateDialog } from '../TicketTemplateDialog';
import { useTemplate } from '../useTemplate';

export const createTicketOtherSchema = ({
  categoryIdKV,
}: {
  categoryIdKV?: string;
}) =>
  z
    .object({
      ...gridOperatorValidationSchema.shape,
      ...categoriesValidationSchema.shape,
      ...assigneeValidationSchema.shape,
      ...attachmentValidationSchema.shape,
      createAnother: z.array(z.string()).optional(),
      description: z
        .string()
        .max(2000)
        .optional()
        .refine((value) => isValidXmlCharacters(value), {
          error: () => ({
            message: 'zod.errors.invalid_xml_characters',
          }),
        }),
      eventDate: z.string().optional(),
      title: z
        .string()
        .min(1, { message: 'zod.errors.required' })
        .max(100, { message: 'zod.errors.max_length' })
        .regex(/^[\p{L}\p{N} _\-.]+$/u, {
          message: 'zod.errors.invalid_characters',
        }),
    })
    .refine(
      (check) => check.mainCategoryId !== categoryIdKV || check.eventDate,
      {
        error: () => ({
          message: 'zod.errors.invalid_event_date',
          path: ['eventDate'],
        }),
      },
    );

type TicketOtherForm = z.infer<ReturnType<typeof createTicketOtherSchema>>;

const { FormCheckbox, FormDate, FormText } = typedFormFields<TicketOtherForm>();

export const TicketCreateOtherDetails: React.FC = () => {
  const { t } = useTranslation();
  const { t: transtic } = useTranslation('ticket');
  const params = useStoredParams('/berichtenbox');
  const navigate = useNavigate();
  const { addToast } = useToast();

  const { data: categoryData } = useGetMainCategory();
  const categoryItems = categoryData?.items ?? [];

  const categoryIdKV = categoryItems.find(
    (cat) => cat.name === 'KV Disputen',
  )?.id;

  const schema = createTicketOtherSchema({ categoryIdKV });

  const validationSchema = schema.superRefine((check, ctx) => {
    const attachmentRequired = categoryItems
      .find(({ id }) => id === check.mainCategoryId)
      ?.subCategories?.find(
        ({ id }) => id === check.subCategoryId,
      )?.attachmentRequired;

    if (attachmentRequired && !check.files?.length) {
      ctx.addIssue({
        code: 'custom',
        message: transtic('create.attachments.required_for_subcategory'),
        path: ['files'],
      });
    }
  });

  const formMethods = useForm<TicketOtherForm>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      title: '',
      regionalGridOperatorId: '',
      mainCategoryId: '',
      subCategoryId: '',
      description: '',
      files: [],
      createAnother: [],
      eventDate: undefined,
    },
  });

  const {
    formState: { isDirty },
    clearErrors,
    getValues,
    handleSubmit,
    reset,
    setError,
    setValue,
    watch,
  } = formMethods;

  const title = watch('title');
  const category = watch('mainCategoryId');
  const subCategory = watch('subCategoryId');
  const files = watch('files');
  const shouldShowEventDate = category === categoryIdKV;
  const [isAttachmentRequired, setIsAttachmentRequired] = useState(false);

  const { showConfirm, onConfirmClick, closeConfirm } = useTemplate<
    typeof schema
  >({
    getValues,
    setValue,
    watch,
  });

  useEffect(() => {
    if (!shouldShowEventDate) {
      reset({
        ...getValues(),
        eventDate: undefined,
      });
    }
  }, [getValues, reset, shouldShowEventDate]);

  useEffect(() => {
    if (!isAttachmentRequired || (files?.length ?? 0) > 0) {
      clearErrors('files');
    }
  }, [clearErrors, files?.length, isAttachmentRequired]);

  const {
    mutate,
    isPending: isLoading,
    isSuccess,
  } = usePostTicket({
    mutation: {
      onError(error) {
        addToast({
          message: transtic('create.toast.create_error.title'),
          title: transtic('create.toast.create_error.message'),
          type: 'error',
          error: error,
        });
      },
      onSuccess: ({ id }) => {
        addToast({
          action: (
            <ToastButton>
              <Link to={`/berichtenbox/${id}`}>
                {transtic('create.toast.create_success.action')}
              </Link>
            </ToastButton>
          ),
          message: transtic('create.toast.create_success.message', { title }),
          title: transtic('create.toast.create_success.title'),
          type: 'success',
        });
      },
    },
  });

  const onSubmit: SubmitHandler<TicketOtherForm> = ({
    createAnother,
    files,
    eventDate,
    ...ticketData
  }) => {
    if (isAttachmentRequired && (!files || files.length === 0)) {
      setError('files', {
        type: 'required',
        message: transtic('create.attachments.required_for_subcategory'),
      });
      return;
    }

    mutate({
      data: {
        ...ticketData,
        attachments: files?.filter(({ file }) => file).map(({ file }) => file),
        eventDate: eventDate?.toString(),
      },
    });
  };

  useEffect(() => {
    if (isSuccess) {
      navigate(
        getValues('createAnother')?.includes('another')
          ? '/berichtenbox/nieuw'
          : '/berichtenbox',
      );
    }
  }, [getValues, isSuccess, navigate]);

  const { relatedArticles, relatedArticlesLoading } =
    useRelatedKnowledgeArticles(category, subCategory);

  return (
    <>
      <PageHead title={transtic('create.page_title')} />
      <Page>
        <PageHeader
          title={transtic('create.other.page_title')}
          backHref="/berichtenbox/nieuw"
          previousText={t('common.steps.previous')}
        />
        <TicketTemplateDialog
          closeConfirm={closeConfirm}
          onConfirmClick={onConfirmClick}
          showConfirm={showConfirm}
        />
        <FormProvider {...formMethods}>
          <form
            className="flex flex-col items-start gap-6 pb-8 md:flex-row"
            onSubmit={handleSubmit(onSubmit)}
          >
            <Card className="flex-2 p-6">
              <Stack gap="lg">
                <Stack gap="md">
                  <Text as="h2" variant="h5">
                    {transtic('create.heading.ticket_about')}
                  </Text>
                  <TicketGridOperatorsFields />
                  <TicketCategoriesFields
                    onAttachmentRequiredChange={setIsAttachmentRequired}
                  />
                  {shouldShowEventDate && (
                    <FormDate
                      label={t('common.event_date')}
                      fullWidth
                      name="eventDate"
                      rules={{ required: true }}
                      disableFutureDates
                    />
                  )}
                </Stack>
                <Stack gap="md">
                  <Text as="h2" variant="h5">
                    {transtic('create.heading.details')}
                  </Text>
                  <FormText
                    label={t('common.subject')}
                    fullWidth
                    name="title"
                    rules={{ maxLength: 100, required: true }}
                    showWordCount
                  />
                  <TicketDescription />
                  {isAttachmentRequired && (!files || files.length === 0) && (
                    <Message variant="warning">
                      <Text>
                        {transtic(
                          'create.attachments.required_for_subcategory',
                        )}
                      </Text>
                    </Message>
                  )}
                  <TicketAttachmentFields isRequired={isAttachmentRequired} />
                </Stack>
                <Divider />
                <Stack direction="row" className="justify-between" gap="lg">
                  <Button
                    as={Link}
                    to={`/berichtenbox${params}`}
                    action="tertiary"
                    emphasis="subtle"
                  >
                    {t('common.button.cancel')}
                  </Button>
                  <Stack direction="row" className="items-center" gap="lg">
                    <FormCheckbox
                      name="createAnother"
                      options={[
                        {
                          label: transtic('create.footer.create_another'),
                          value: 'another',
                        },
                      ]}
                    />
                    <Button type="submit" isLoading={isLoading}>
                      {t('common.submit')}
                    </Button>
                  </Stack>
                </Stack>
              </Stack>
            </Card>
            <Stack as="aside" gap="lg" className="flex-1">
              <TicketAssigneeCard />
              {category && (
                <RelatedKnowledgeArticlesCard
                  articles={relatedArticles}
                  isLoading={relatedArticlesLoading}
                />
              )}
            </Stack>
          </form>
        </FormProvider>
        <CancelCreateTicketDialog shouldBlock={isDirty && !isSuccess} />
      </Page>
    </>
  );
};
